/**
 * Campaign asset files (S3-backed, referenced by object key on `CampaignAsset`).
 *
 * A campaign asset is a file object — a deck, PDF, image or doc — referenced by an `object_key`.
 * The row is plain `CampaignAsset` CRUD, but the bytes are uploaded the same three-step way
 * report-figure images and journey-map attachments are, so the bytes never pass through the API and
 * a client cannot misreport an object's size or type:
 *
 *   1. POST `db/campaign-asset/presign/`            -> short-lived signed PUT + object key
 *   2. PUT the file straight at storage
 *   3. write the CampaignAsset row (generic CRUD) with the returned `object_key`
 *
 * Step 3 is NOT a bespoke create endpoint here — the asset row is written by the admin editor
 * through `useCampaignMutations('asset')`, so `uploadCampaignAsset` stops after the PUT and returns
 * the object key + type + size for the caller to store on the row.
 *
 * Unlike `campaignReportFigures`, there is NO client-side image shrink: an asset is an arbitrary
 * file (a 40 MB deck, a PDF), so the raw `File` is presigned and PUT as-is. `size_bytes` is the
 * file's own size; there are no image dimensions.
 *
 * Reads are signed GET URLs fetched per use (`db/campaign-asset/{id}/download-url/`). They expire,
 * so they are never held in state; a download resolves one on demand. The signed read uses
 * `attachment` disposition — an asset is downloaded, not previewed inline.
 *
 * The presign key prefix is `campaign-assets/<campaign>/…`. This feature is inert until the bucket
 * is set AND CORS + the node-role grant are in place per environment (the API answers 503 while the
 * bucket is unset).
 */
import { buildApiUrl, getApiHeaders } from './queriesHelpers';
import { UploadProgressCallback, putFileToStorageWithProgress } from './xhrUpload';

export type CampaignAssetPresign = {
  upload_url: string;
  object_key: string;
  content_type: string;
  file_name: string;
  expires_in: number;
  max_bytes: number;
};

export type CampaignAssetDownload = {
  url: string;
  expires_in: number;
  refresh_after: number;
  signed: boolean;
  file_name: string;
  content_type: string;
  size_bytes: number;
};

/** What `uploadCampaignAsset` hands back for the caller to store on the asset row. */
export type UploadedCampaignAsset = {
  object_key: string;
  content_type: string;
  size_bytes: number;
};

/** The S3 bucket is unset, so the whole asset-file feature is inert (API answers 503). */
export class CampaignAssetStorageUnavailableError extends Error {}

/**
 * State of an asset's in-app preview derivative (mirrors the resource-file preview contract).
 *
 * The pptx→PDF conversion is asynchronous, so the endpoint reports one of:
 *   ready       — `render_kind: 'pdf'` with a signed `preview_url` the PDF slide viewer renders,
 *                 or `render_kind: 'native'` for a file the browser renders itself (image/pdf).
 *   processing  — enqueued, not built yet; `preview_url` is null. Re-request shortly.
 *   failed      — the conversion failed; `preview_url` is null and the caller keeps the download.
 *   none        — no preview applies to this file kind; `preview_url` is null.
 * `preview_url` is non-null only when `preview_status === 'ready'`.
 */
export type CampaignAssetRenderKind = 'pdf' | 'native' | 'none';
export type CampaignAssetPreviewStatus = 'ready' | 'processing' | 'failed' | 'none';
export type CampaignAssetPreview = {
  render_kind: CampaignAssetRenderKind;
  preview_url: string | null;
  preview_status: CampaignAssetPreviewStatus;
};

const PRESIGN_ENDPOINT = 'db/campaign-asset/presign/';
const downloadEndpoint = (assetId: number | string) =>
  `db/campaign-asset/${assetId}/download-url/`;
const previewEndpoint = (assetId: number | string) =>
  `db/campaign-asset/${assetId}/preview-url/`;
const assetEndpoint = (assetId: number | string) => `db/campaign-asset/${assetId}`;

/** Presign and download bodies normalize errors to `detail`; field errors still come back keyed. */
const errorMessage = (payload: any, status: number, fallback: string): string => {
  const detail = payload?.detail ?? payload?.object_key ?? payload?.file_name ?? payload?.campaign;
  if (Array.isArray(detail) && detail.length) return String(detail[0]);
  if (detail) return String(detail);
  if (typeof payload === 'string' && payload.trim()) return payload;
  return `${fallback} (${status})`;
};

async function assetApiFetch<T = any>(
  method: 'GET' | 'POST' | 'DELETE',
  endpoint: string,
  options: { params?: Record<string, any>; body?: any } = {},
): Promise<T> {
  const { params = {}, body } = options;
  const headers = { ...getApiHeaders(null, method, endpoint) } as Record<string, string>;

  const response = await fetch(buildApiUrl(endpoint, params, true), {
    method,
    headers,
    credentials: 'include',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  const text = await response.text();
  let parsed: any = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const message = errorMessage(parsed, response.status, 'Campaign asset request failed');
    if (response.status === 503) throw new CampaignAssetStorageUnavailableError(message);
    const error: any = new Error(message);
    error.status = response.status;
    error.payload = parsed;
    throw error;
  }

  return parsed as T;
}

export async function presignCampaignAssetUpload(
  campaignId: number | string,
  file: { name: string; type?: string },
  meta: { kind?: string; key?: string } = {},
): Promise<CampaignAssetPresign> {
  return await assetApiFetch<CampaignAssetPresign>('POST', PRESIGN_ENDPOINT, {
    body: {
      campaign: campaignId,
      kind: meta.kind,
      key: meta.key,
      file_name: file.name,
      content_type: file.type || 'application/octet-stream',
    },
  });
}

/**
 * Upload the bytes of a file the admin picked to storage, returning the object key, type and size
 * for the caller to store on a `CampaignAsset` row.
 *
 * The raw `File` is presigned and PUT as-is — no shrink, because an asset is an arbitrary file, not
 * an image. Throws on a file whose type isn't on the allowlist, one over the size limit, or when
 * storage is unavailable.
 */
export async function uploadCampaignAsset(
  campaignId: number | string,
  file: File,
  meta: { kind?: string; key?: string } = {},
  options: { onProgress?: UploadProgressCallback } = {},
): Promise<UploadedCampaignAsset> {
  const presign = await presignCampaignAssetUpload(campaignId, file, meta);
  if (presign.max_bytes && file.size > presign.max_bytes) {
    throw new Error(
      `That file is larger than the ${Math.round(presign.max_bytes / (1024 * 1024))} MB limit.`,
    );
  }

  await putFileToStorageWithProgress(
    presign.upload_url,
    file,
    presign.content_type || file.type || 'application/octet-stream',
    options.onProgress,
  );

  return {
    object_key: presign.object_key,
    content_type: presign.content_type || file.type || 'application/octet-stream',
    size_bytes: file.size,
  };
}

export async function getCampaignAssetDownload(
  assetId: number | string,
  /**
   * A friendly name for the downloaded file. The signed GET sets it as the `Content-Disposition`
   * filename, so a caller that knows the file's role in context (e.g. "Frontier AI Workshop Deck")
   * passes it here rather than letting the browser fall back to the object key's opaque hash. The
   * backend sanitizes it and guarantees the right extension for the stored content type.
   */
  filename?: string,
): Promise<CampaignAssetDownload> {
  return await assetApiFetch<CampaignAssetDownload>('GET', downloadEndpoint(assetId), {
    params: filename ? { filename } : {},
  });
}

/**
 * Fetch the state of an asset's in-app preview (e.g. the pptx→PDF derivative of a deck).
 *
 * Like the resource-file preview, the conversion is asynchronous, so this returns `ready`
 * (with a signed `preview_url`), `processing` (re-request shortly), `failed`, or `none`.
 * The signed `preview_url` is cross-origin S3, so under COEP `require-corp` the caller must fetch
 * it into a same-origin `blob:` URL before rendering it (see StepFilesFields / ResourceFileViewer).
 */
export async function getCampaignAssetPreview(
  assetId: number | string,
): Promise<CampaignAssetPreview> {
  return await assetApiFetch<CampaignAssetPreview>('GET', previewEndpoint(assetId));
}

/**
 * Delete a campaign asset row (generic CRUD DELETE). The row and its S3 object go away.
 *
 * Admin editors normally delete through `useCampaignMutations('asset').remove` so the query cache
 * invalidates; this standalone helper is for callers outside a mutation hook.
 */
export async function deleteCampaignAsset(assetId: number | string): Promise<void> {
  await assetApiFetch<unknown>('DELETE', assetEndpoint(assetId));
}
